import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

export type InsightTileGroupLayout =
    | 'picture-six'
    | 'row-four'
    | 'six'
    | 'two-by-two';

const pictureSixAreas = [
  'tile1',
  'tile2',
  'tile3',
  'tile4',
  'tile5',
  'tile6',
] as const;

type FourTileTuple = readonly [ReactNode, ReactNode, ReactNode, ReactNode];
type SixTileTuple = readonly [
    ReactNode,
    ReactNode,
    ReactNode,
    ReactNode,
    ReactNode,
    ReactNode,
];

interface InsightTileGroupBaseProps {
    sx?: SxProps<Theme>;
}

interface PictureSixInsightTileGroupProps extends InsightTileGroupBaseProps {
    layout: 'picture-six';
    picture: ReactNode;
    insights: SixTileTuple;
}

interface RowFourInsightTileGroupProps extends InsightTileGroupBaseProps {
    layout: 'row-four';
    insights: FourTileTuple;
}

interface TwoByTwoInsightTileGroupProps extends InsightTileGroupBaseProps {
    layout: 'two-by-two';
    insights: FourTileTuple;
}

interface SixInsightTileGroupProps extends InsightTileGroupBaseProps {
    layout: 'six';
    insights: SixTileTuple;
}

export type InsightTileGroupProps =
    | PictureSixInsightTileGroupProps
    | RowFourInsightTileGroupProps
    | TwoByTwoInsightTileGroupProps
    | SixInsightTileGroupProps;

const renderTiles = (tiles: readonly ReactNode[], sx: SxProps<Theme>) => (
  <Box sx={sx}>
    {tiles.map((tile, index) => (
      <Box
        key={`insight-tile-${index}`}
        sx={{ minWidth: 0, height: '100%' }}
      >
        {tile}
      </Box>
    ))}
  </Box>
);

// Fixed 48px gap between picture and tiles, and between tile rows/columns, for consistency across pages.
const GAP = 6;

export const InsightTileGroup = (props: InsightTileGroupProps) => {
  const { sx } = props;

  if (props.layout === 'picture-six') {
    return (
      <Box sx={sx}>
        <Box
          data-layout={props.layout}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr)',
            },
            gridTemplateAreas: {
              xs: '"image" "tile1" "tile2" "tile3" "tile4" "tile5" "tile6"',
              md: '"image tile1 tile2" "image tile3 tile4" "image tile5 tile6"',
            },
            gap: GAP,
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              gridArea: 'image',
              display: 'flex',
              minWidth: 0,
              height: '100%',
              '& > *': {
                width: '100%',
                height: '100%',
              },
            }}
          >
            {props.picture}
          </Box>
          {props.insights.map((tile, index) => (
            <Box
              key={`picture-six-insight-${index}`}
              sx={{
                gridArea: pictureSixAreas[index],
                minWidth: 0,
                height: '100%',
              }}
            >
              {tile}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (props.layout === 'row-four') {
    return (
      <Box sx={sx}>
        <Box data-layout={props.layout}>
          {renderTiles(props.insights, {
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: GAP,
            minWidth: 0,
          })}
        </Box>
      </Box>
    );
  }

  if (props.layout === 'two-by-two') {
    return (
      <Box sx={sx}>
        <Box data-layout={props.layout}>
          {renderTiles(props.insights, {
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
            },
            gap: GAP,
            minWidth: 0,
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      <Box data-layout={props.layout}>
        {renderTiles(props.insights, {
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
          gap: GAP,
          minWidth: 0,
        })}
      </Box>
    </Box>
  );
};
